import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store';
import * as DriverActions from '@app/store/driver/actions/driver.actions';
import * as driverStateSelectors from '@app/store/driver/selectors/driver.selector';
import { filter, map } from 'rxjs/operators';
import { selectChosenAsset } from '@app/store/asset/selectors/assets.selectors';
import { getDateTimeText, toTitleCase } from '@app/modules/shared/utilities/utilities';
import { DriverProfile, DisplayDriver } from '@app/modules/driver/models/driver.models';
import { Observable } from 'rxjs';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { AppConfigService } from '@app/services/app-config/app-config.service';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class DriverFacade {
  constructor(private store: Store<AppState>, private appConfigService: AppConfigService) {}

  unknownSourceText = 'Unknown Source'; // ZTT-2920 to translate
  unknownTsText = 'Unknown Time'; // ZTT-2920 to translate
  unknownDriver = {
    isUnknown: true,
    sourceLabel: null,
    source: null
  };

  setDriverProfileById(driverProfileId) {
    this.store.dispatch(DriverActions.loadDriverProfile({ driverProfileId }));
  }

  getDriverProfile() {
    return this.store.select(driverStateSelectors.selectDriverProfile);
  }

  getDriverProfileLoadState() {
    return this.store.select(driverStateSelectors.selectDriverProfileLoadState);
  }

  clearDriverProfile() {
    return this.store.dispatch(DriverActions.clearDriverProfile());
  }

  formatDriverAssociationText(driverChangeSource: string): string {
    return driverChangeSource ? toTitleCase(driverChangeSource) : this.unknownSourceText;
  }
  getDriverEventTimestamp(driverEventTs: string, dateTimeFormat = 'MM/DD/YYYY HH:mm:ss'): string {
    let { timeZone } = this.appConfigService.getConfig();
    timeZone = timeZone || this.appConfigService.getLocalTimeZone();
    return driverEventTs ? getDateTimeText(driverEventTs, dateTimeFormat, timeZone) : this.unknownTsText;
  }

  getDriverAssociationSourceAndTime(dateTimeFormat = 'MM/DD/YYYY HH:mm:ss') {
    return this.store.select(selectChosenAsset).pipe(
      filter(selectedAsset => Boolean(selectedAsset)),
      // Yes, I hate this, but why are we creating
      map(selectedAsset => {
        const driverChangeApplicationName = this.formatDriverAssociationText(selectedAsset.driverChangeSource);
        const dateTimeText = this.getDriverEventTimestamp(selectedAsset.driverEventTs, dateTimeFormat);
        return `${driverChangeApplicationName} at ${dateTimeText}`;
      })
    );
  }

  getDriverAssetProfileLoadState() {
    return this.store.select(driverStateSelectors.selectDriverAssetProfileLoadState);
  }

  getDriverAssetProfile(): Observable<DriverProfile[]> {
    return this.store.select(driverStateSelectors.selectDriverAssetProfile);
  }

  /**
   * Checks against each driver association in store, and keeps the most recent applicable
   * association event, per driver, for the trip period determined by start/end timestamps.
   * @param startTimestamp Timestamp for the beginning of the Trip
   * @param endTimestamp Timestamp for the end of the Trip
   */
  getDriverAssetProfilesForTrip(startTimestamp: string, endTimestamp: string): Observable<DisplayDriver[]> {
    return this.store.select(driverStateSelectors.selectDriverAssetProfile).pipe(
      map(profiles => {
        let profilesForTrip = [...profiles];
        // sort into reverse chronological order for later logic
        profilesForTrip.sort((a, b) =>
          a.startTimestamp > b.startTimestamp ? -1 : a.startTimestamp < b.startTimestamp ? 1 : 0
        );

        // filter out associations ending bitwen the startTimestamp and endTimestamp or after the endTimestamp
        profilesForTrip = profilesForTrip.filter(
          p =>
            p.startTimestamp <= endTimestamp &&
            (p.endTimestamp === null || p.endTimestamp === undefined || p.endTimestamp >= startTimestamp)
        );

        // put them back in true chronological order
        profilesForTrip.reverse();

        return profilesForTrip;
      }),
      map(profiles => this.insertUnknownDrivers(profiles, startTimestamp, endTimestamp))
    );
  }

  /**
   * Inserts unknown driver profiles into intervals where no driver is specified.
   *
   * @param driverProfiles - An array of driver profiles (`DriverProfile[]`) to process.
   * @param startTimestamp - A string representing the start timestamp for interval checking.
   * @param endTimestamp - A string representing the end timestamp for interval checking.
   *
   * @returns An array (`any[]`) containing the original driver profiles and the inserted unknown driver profiles.
   * Each unknown driver object includes the properties `isUnknown`, `startTimestamp`, `endTimestamp`, `sourceLabel`, and `source`.
   *
   * @remarks
   * - Unknown driver profiles are inserted when there is a gap at the beginning, between two driver profiles, or at the end.
   * - Unknown drivers have `isUnknown` set to `true` and other relevant properties set to `null` or appropriate values.
   * - This method alters the order and content of the `driverProfiles` array but does not modify the existing `DriverProfile` objects themselves.
   */
  insertUnknownDrivers(driverProfiles: DriverProfile[], startTimestamp: string, endTimestamp: string): DisplayDriver[] {
    const result: DisplayDriver[] = [];

    driverProfiles.forEach((currentDriver, index) => {
      if (index === 0) {
        // Check if there is a hole at the beginning.
        if (startTimestamp < currentDriver.startTimestamp) {
          result.push({
            ...this.unknownDriver,
            startTimestamp: startTimestamp,
            endTimestamp: currentDriver.startTimestamp
          });
        }
      } else {
        const previousDriver = driverProfiles[index - 1];
        // Check if there is a gap between the previous driver and the current driver.
        if (
          DateTime.fromISO(previousDriver.endTimestamp, { zone: 'utc' }).diff(
            DateTime.fromISO(currentDriver.startTimestamp, { zone: 'utc' }),
            'seconds'
          ).seconds > 2
        ) {
          result.push({
            ...this.unknownDriver,
            startTimestamp: previousDriver.endTimestamp,
            endTimestamp: currentDriver.startTimestamp
          });
        }
      }

      result.push({ ...currentDriver, isUnknown: false });

      // Check if there is a hole at the end after the last driver.
      if (index === driverProfiles.length - 1 && currentDriver.endTimestamp < endTimestamp) {
        result.push({
          ...this.unknownDriver,
          startTimestamp: currentDriver.endTimestamp,
          endTimestamp: endTimestamp
        });
      }
    });

    return result;
  }

  /**
   * Takes an array of DriverProfiles and returns an array which has been filtered to include only those with a startTimestamp matching
   * the day of the timestamp param, and then sorts them into reverse-chronological order.
   * @param profiles Array of DriverProfiles
   * @param timestamp Timestamp to match the day of
   */
  sortProfilesForDay(profiles: DriverProfile[], timestamp: string): DriverProfile[] {
    const dateCheck = new Date(timestamp).getDate();
    profiles = profiles.filter(p => new Date(p.startTimestamp).getDate() == dateCheck);
    profiles.sort((a, b) => (a.startTimestamp > b.startTimestamp ? -1 : a.startTimestamp < b.startTimestamp ? 1 : 0));
    return profiles;
  }

  /**
   * Takes a reverse-chronological sorted array of DriverProfiles and returns the Profile
   * which was the most recently associated prior to the provided timestamp
   * @param profiles Array of DriverProfiles
   * @param timestamp Timestamp to match the driver against
   */
  getDriverProfileAtTimestamp(profiles: DriverProfile[], timestamp: string): DriverProfile {
    profiles.sort((a, b) => (a.startTimestamp > b.startTimestamp ? -1 : a.startTimestamp < b.startTimestamp ? 1 : 0));
    return profiles.find(
      p =>
        p.startTimestamp <= timestamp &&
        (p.endTimestamp === null || p.endTimestamp === undefined || p.endTimestamp >= timestamp)
    );
  }

  setDriverAssetProfile(asset: ViewableAsset) {
    this.store.dispatch(DriverActions.loadDriverAssetProfile({ asset }));
  }

  setDriverAssetProfileDirect(driverAssetProfile) {
    this.store.dispatch(DriverActions.loadDriverAssetProfileSuccess({ driverAssetProfile }));
  }

  setDriverAssetProfileError(driverAssetProfileFailure) {
    this.store.dispatch(DriverActions.loadDriverAssetProfileFailure({ driverAssetProfileFailure }));
  }
}
